import {
  Button,
  Flex,
  FormField,
  InputPasswordField,
  InputTextField,
  Row,
  Switch,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { type KeyboardEvent } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useSignUp } from '~/domain/SignUp';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { useTrackEvent } from '~/hooks/useTrackEvent';
import { AuthErrorBanner, useAuthFormSubmit, useCancelPartialSession } from '../shared';
import { FreeToJoinBanner } from './FreeToJoinBanner';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;
const TERMS_URL = 'https://gotu.com/client-terms-of-use/';

const activateOnSpaceOrEnter = (handler: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

export interface ISignUpAccountFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

export const SignUpForm = () => {
  const methods = useForm<ISignUpAccountFormValues>({
    defaultValues: { email: '', password: '', confirmPassword: '', termsAccepted: false },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <SignUpFormInner />
    </FormProvider>
  );
};

// Order for picking which specific validation message to surface in the banner.
const FIELD_ORDER: Array<keyof ISignUpAccountFormValues> = [
  'email',
  'password',
  'confirmPassword',
];

const SignUpFormInner = () => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const { mutate: signUp, isPending } = useSignUp();
  const cancelPartialSession = useCancelPartialSession();

  const { submit, bannerVisible, bannerRef } = useAuthFormSubmit<ISignUpAccountFormValues>({
    onValid: ({ email, password }) => {
      trackEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_CLICKED);
      const referralId = localStorage.getItem('referralId') || undefined;
      signUp(
        { email, password, referralId },
        { onSuccess: () => navigate('/signup/your-info') },
      );
    },
  });

  const {
    register,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<ISignUpAccountFormValues>();

  const goToLogin = cancelPartialSession;
  const openTerms = () => window.open(TERMS_URL, '_blank', 'noopener,noreferrer');

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: { value: EMAIL_REGEX, message: 'The email address entered is invalid.' },
  });

  const specificErrorMessage = FIELD_ORDER.map((key) => errors[key])
    .filter((err): err is NonNullable<typeof err> => Boolean(err) && err?.type !== 'required')
    .map((err) => err.message as string | undefined)
    .find(Boolean);

  return (
    <form onSubmit={submit} data-testid="signup-form" noValidate>
      <Typography bold color={colors.primary[500]} style={{ fontSize: 28, marginTop: 27 }}>
        Welcome, Offices!
      </Typography>
      <Typography
        size="body_large"
        color={colors.neutral[500]}
        style={{ display: 'block', marginTop: 18, lineHeight: 1.4 }}
      >
        Create your account and join thousands of offices using GoTu to find trusted dental
        professionals quickly.
      </Typography>

      <div style={{ marginTop: 24 }}>
        <FreeToJoinBanner />
      </div>

      {/* Stable scrollIntoView target when the banner is hidden. */}
      <div ref={bannerRef} style={{ marginTop: bannerVisible ? 12 : 0 }}>
        <AuthErrorBanner visible={bannerVisible} message={specificErrorMessage} />
      </div>

      <div style={{ marginTop: bannerVisible ? 12 : 20 }}>
        <FormField label="Email">
          <InputTextField
            data-cy="input-email"
            placeholder="Enter your email"
            isError={Boolean(errors.email)}
            {...emailRegister}
            onChange={(e) => {
              // Strip whitespace as the user types so a stray space (typed
              // or pasted, leading or trailing) never reaches the regex.
              const stripped = e.target.value.replace(/\s+/g, '');
              if (stripped !== e.target.value) {
                e.target.value = stripped;
              }
              emailRegister.onChange(e);
            }}
          />
        </FormField>
      </div>

      <div style={{ marginTop: 24 }}>
        <FormField label="Password">
          <InputPasswordField
            data-cy="input-password"
            placeholder="Enter your password"
            isError={Boolean(errors.password)}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `All passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`,
              },
            })}
          />
        </FormField>
      </div>

      <div style={{ marginTop: 24 }}>
        <FormField label="Confirm Password">
          <InputPasswordField
            data-cy="input-confirm-password"
            placeholder="Confirm your password"
            isError={Boolean(errors.confirmPassword)}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) =>
                value === getValues('password') || 'Passwords do not match. Please try again.',
            })}
          />
        </FormField>
      </div>

      <Row justify="center" style={{ marginTop: 32 }}>
        <Controller
          name="termsAccepted"
          control={control}
          rules={{ validate: (v) => v === true || 'You must accept the Terms and Conditions' }}
          render={({ field, fieldState }) => (
            <Switch
              data-cy="switch-terms-accepted"
              checked={field.value}
              onChange={field.onChange}
              error={Boolean(fieldState.error)}
              label={
                <Typography size="body_large" color={colors.neutral[500]}>
                  I agree to GoTu&apos;s{' '}
                  <Typography
                    as="span"
                    bold
                    color={colors.secondary[500]}
                    style={{ fontSize: 18, cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    data-cy="link-terms-and-conditions"
                    onClick={openTerms}
                    onKeyDown={activateOnSpaceOrEnter(openTerms)}
                  >
                    Terms and Conditions
                  </Typography>
                </Typography>
              }
            />
          )}
        />
      </Row>

      <Flex justify="center" style={{ marginTop: 32 }}>
        <Button
          data-cy="btn-sign-up"
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isPending}
          style={{ width: 296, borderRadius: 999 }}
        >
          Start Finding Coverage
        </Button>
      </Flex>

      <Row justify="center" align="center" wrap="nowrap" gap={1} style={{ marginTop: 24 }}>
        <Typography size="body_large" color={colors.neutral[500]}>
          Already have an account?
        </Typography>
        <Typography
          as="span"
          bold
          color={colors.secondary[500]}
          style={{ fontSize: 18, cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          data-cy="link-log-in"
          onClick={goToLogin}
          onKeyDown={activateOnSpaceOrEnter(goToLogin)}
        >
          Log In
        </Typography>
      </Row>
    </form>
  );
};
